import {Button, Card, Col, Modal, Row} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {Fragment, useEffect, useMemo, useRef, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {toast} from 'react-toastify'
import {copyToClipboard} from '../../../../../utils/clipboardUtils'
import {GET_ONE_POLICY_API} from '../../../gql/queries/policiesApiQueries'
import {GET_MARKET_PLACE_2} from '../../../gql/queries/leadsQuery'
import {IResOnePolicyApi, NodePolicy} from '../../../types/policiesApi'
import {IResTest} from '../../../types/leads'
import {Plan} from '../../oneLead/oneleadTypes'
import {parseId} from '../../../helpers'
import {getMetalLevelValue} from '../../../helpers/consts'
import {
  buildMarketplaceLookupVariables,
  buildPolicyCardData,
  canLookupMarketplaceCardData,
  parseMarketplacePlanResponse,
} from './idCardData'
import {openPrintableMemberIdCard} from './idCardPrint'

interface ModalMemberIdCardProps {
  isOpen: boolean
  onClose: () => void
  id: string
}

const M = defineMessages({
  title: {id: 'policies.list.idCard.title', defaultMessage: 'Get member ID card'},
  loadingPolicy: {id: 'policies.list.idCard.loadingPolicy', defaultMessage: 'Loading policy...'},
  errorPolicy: {id: 'policies.list.idCard.errorPolicy', defaultMessage: 'Error loading policy data.'},
  sourceLabel: {id: 'policies.list.idCard.sourceLabel', defaultMessage: 'Source:'},
  sourcePolicyMarketplace: {id: 'policies.list.idCard.sourcePolicyMarketplace', defaultMessage: 'Policy + Marketplace'},
  sourcePolicyOnly: {id: 'policies.list.idCard.sourcePolicyOnly', defaultMessage: 'Policy only'},
  copyJson: {id: 'policies.list.idCard.copyJson', defaultMessage: 'Copy card JSON'},
  printPdf: {id: 'policies.list.idCard.printPdf', defaultMessage: 'Print / Save PDF'},
  loadingMarketplace: {id: 'policies.list.idCard.loadingMarketplace', defaultMessage: 'Loading marketplace plan details...'},
  marketplaceFailed: {id: 'policies.list.idCard.marketplaceFailed', defaultMessage: 'Marketplace lookup failed: {message}. Showing local fields only.'},
  front: {id: 'policies.list.idCard.front', defaultMessage: 'Front'},
  back: {id: 'policies.list.idCard.back', defaultMessage: 'Back'},
  toastPopups: {id: 'policies.list.idCard.toastPopups', defaultMessage: 'Please allow pop-ups to print or save the card as PDF.'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
})

const hasDisplayValue = (value?: string | null) => {
  const text = String(value || '').trim()
  return text.length > 0 && text.toUpperCase() !== 'N/A' && text !== 'N/A / N/A'
}

export const ModalMemberIdCard: React.FC<ModalMemberIdCardProps> = ({isOpen, onClose, id}) => {
  const intl = useIntl()
  const [info, setInfo] = useState<NodePolicy | null>(null)
  const [marketPlan, setMarketPlan] = useState<Plan | null>(null)
  const [marketLookupError, setMarketLookupError] = useState<string | null>(null)
  const lookupKeyRef = useRef<string>('')

  const [gqlGetPolicy, {data: dataPolicy, loading: loadingPolicy, error: errorPolicy}] =
    useLazyQuery<IResOnePolicyApi>(GET_ONE_POLICY_API, {fetchPolicy: 'no-cache'})
  const [gqlGetMarketplace, {data: dataMarketplace, loading: loadingMarketplace}] =
    useLazyQuery<IResTest>(GET_MARKET_PLACE_2, {fetchPolicy: 'no-cache'})

  useEffect(() => {
    if (!isOpen || !id) return
    gqlGetPolicy({variables: {id: parseId(id)}})
  }, [gqlGetPolicy, id, isOpen])

  useEffect(() => {
    if (!dataPolicy?.policiesById) return
    setInfo(dataPolicy.policiesById)
    setMarketPlan(null)
    setMarketLookupError(null)
    lookupKeyRef.current = ''
  }, [dataPolicy])

  useEffect(() => {
    if (!dataMarketplace?.marketplace2) return
    const {plan, errorMessage} = parseMarketplacePlanResponse(dataMarketplace.marketplace2, {
      planName: info?.planName,
      issuerName: info?.carrier?.name,
      metalLevel: info ? getMetalLevelValue(String(info.metalLevel || '')) : '',
    })
    setMarketPlan(plan)
    setMarketLookupError(errorMessage)
  }, [dataMarketplace, info])

  useEffect(() => {
    if (!info) return
    const variables = buildMarketplaceLookupVariables(info)
    if (!variables) return

    const lookupKey = JSON.stringify({
      policyId: info.id,
      cmsPlanId: info.CMSPlanId,
      planName: info.planName,
      carrier: info.carrier?.name,
      metalLevel: info.metalLevel,
      state: info.stateEU?.code,
      effectiveDate: info.effectiveDate,
      membersCount: info.memberPolicySet?.edges?.length || 0,
    })
    if (lookupKeyRef.current === lookupKey) return

    lookupKeyRef.current = lookupKey
    setMarketLookupError(null)
    gqlGetMarketplace({variables})
  }, [gqlGetMarketplace, info])

  const cardData = useMemo(() => {
    if (!info) return null
    return buildPolicyCardData(info, marketPlan)
  }, [info, marketPlan])

  const frontRows = useMemo(
    () =>
      cardData
        ? [
            ['Subscriber', cardData.subscriberName],
            ['Member', cardData.memberName],
            ['Member ID', cardData.memberId],
            ['Policy #', cardData.policyId],
            ['Plan', cardData.planName],
            ['Effective Date', cardData.effectiveDate],
            ['Type / Metal', `${cardData.planType} / ${cardData.metalLevel}`],
            ['Deductible (Ind/Fam)', cardData.medicalDeductible],
            ['OOPM (Ind/Fam)', cardData.annualOopm],
            ['PCP', cardData.pcp],
            ['Specialist', cardData.specialist],
            ['Urgent Care', cardData.urgentCare],
            ['ER', cardData.emergencyRoom],
            ['Rx Generic / Brand', `${cardData.genericRx} / ${cardData.preferredBrandRx}`],
          ].filter(([, value]) => hasDisplayValue(value))
        : [],
    [cardData]
  )

  const backRows = useMemo(() => {
    if (!cardData) return []
    const rows: Array<[string, string]> = [
      ['Carrier', cardData.carrierName],
      ['Member Services', cardData.memberServicesPhone],
      ['Provider', cardData.providerPhone],
      ['Pharmacy', cardData.pharmacyPhone],
      ['Nurse Line', cardData.nurseLine],
      ['Billing', cardData.billingPhone],
      ['Provider Help Desk', cardData.providerHelpDesk],
      ['Prior Auth', cardData.priorAuthPhone],
      ['Care Team', cardData.careTeamPhone],
      ['Mental Health', cardData.mentalHealthPhone],
      ['Medical Claims', cardData.medicalClaimsAddress],
      ['Pharmacy Claims', cardData.pharmacyClaimsAddress],
      ['Claims - Mental Health', cardData.claimsMentalHealth],
      ['Claims - Pharmacy', cardData.claimsPharmacy],
      ['Claims - Pediatric Vision', cardData.claimsPediatricVision],
      ['Claims - Medical', cardData.claimsMedical],
      ['Payer ID', cardData.payerId],
      ['Website', cardData.website],
      ['Provider Portal', cardData.providerPortal],
      ['Issuer TTY', cardData.issuerTty],
      ['Referral', cardData.referralRequired],
      ['Rx BIN', cardData.rxBin],
      ['Rx PCN', cardData.rxPcn],
      ['Rx GRP', cardData.rxGrp],
      ['Benefits URL', cardData.benefitsUrl],
      ['Formulary URL', cardData.formularyUrl],
      ['Network URL', cardData.providerNetworkUrl],
    ]
    cardData.additionalBackLines.forEach((line, index) => rows.push([`Note ${index + 1}`, line]))
    return rows.filter(([, value]) => hasDisplayValue(value))
  }, [cardData])

  const canLookupMarketplace = info ? canLookupMarketplaceCardData(info) : false

  return (
    <Modal show={isOpen} onHide={onClose} backdrop='static' keyboard={false} dialogClassName='modal-wide-sm'>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{backgroundColor: '#f2f3f8'}} className='modal-body-policy-preview'>
        {loadingPolicy && <div className='text-center'>{intl.formatMessage(M.loadingPolicy)}</div>}
        {errorPolicy && <div className='text-center text-danger'>{intl.formatMessage(M.errorPolicy)}</div>}
        {!loadingPolicy && !errorPolicy && cardData && (
          <Card className='policy-id-card-information'>
            <Card.Body>
              <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
                <div className='small text-muted'>
                  {intl.formatMessage(M.sourceLabel)} <strong>
                    {cardData.source === 'LOCAL_AND_MARKETPLACE'
                      ? intl.formatMessage(M.sourcePolicyMarketplace)
                      : intl.formatMessage(M.sourcePolicyOnly)}
                  </strong>
                </div>
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => copyToClipboard(JSON.stringify(cardData, null, 2), 'ID card data JSON')}
                >
                  {intl.formatMessage(M.copyJson)}
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => {
                    const opened = openPrintableMemberIdCard(cardData)
                    if (!opened) toast.error(intl.formatMessage(M.toastPopups))
                  }}
                >
                  {intl.formatMessage(M.printPdf)}
                </Button>
              </div>
              {loadingMarketplace && canLookupMarketplace && (
                <div className='small text-info mb-2'>{intl.formatMessage(M.loadingMarketplace)}</div>
              )}
              {marketLookupError && (
                <div className='small text-warning mb-2'>
                  {intl.formatMessage(M.marketplaceFailed, {message: marketLookupError})}
                </div>
              )}
              {!canLookupMarketplace && info?.CMSPlanId && (
                <div className='small text-warning mb-2'>
                  <FormattedMessage
                    id='policies.list.idCard.cmsFederalOnly'
                    defaultMessage='CMS Plan lookup is only available when state jurisdiction is <b>FEDERAL</b>.'
                    values={{b: (chunks) => <strong>{chunks}</strong>}}
                  />
                </div>
              )}
              <Row className='g-3'>
                <Col md={6}>
                  <div className='id-card-box'>
                    <div className='id-card-box__title'>{intl.formatMessage(M.front)}</div>
                    <div className='id-card-grid'>
                      {frontRows.map(([label, value]) => (
                        <Fragment key={`front-row-${label}`}>
                          <div>{label}</div>
                          <div>{value}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className='id-card-box'>
                    <div className='id-card-box__title'>{intl.formatMessage(M.back)}</div>
                    <div className='id-card-grid'>
                      {backRows.map(([label, value]) => (
                        <Fragment key={`back-row-${label}`}>
                          <div>{label}</div>
                          <div>{value}</div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' size='sm' onClick={onClose}>
          {intl.formatMessage(M.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
